import { TranslationResources } from "./translationResources";

const TRANSLATION_CACHE_PREFIX = "economus:translations:";

interface CachedTranslationResources {
  hash: string;
  resources: TranslationResources;
  savedAt: number;
}

const getCacheKey = (lang: string) => `${TRANSLATION_CACHE_PREFIX}${lang}`;

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  return `{${Object.keys(value as Record<string, unknown>)
    .sort()
    .map((key) => {
      const nextValue = (value as Record<string, unknown>)[key];
      return `${JSON.stringify(key)}:${stableStringify(nextValue)}`;
    })
    .join(",")}}`;
};

const hashString = (value: string): string => {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
};

export const getTranslationHash = (resources: TranslationResources) =>
  hashString(stableStringify(resources));

export const getCachedTranslationResources = (
  lang: string,
): CachedTranslationResources | null => {
  try {
    const cachedValue = localStorage.getItem(getCacheKey(lang));
    if (!cachedValue) return null;

    const parsedValue = JSON.parse(cachedValue) as CachedTranslationResources;
    if (!parsedValue.hash || !parsedValue.resources) return null;

    return parsedValue;
  } catch (error) {
    console.warn(`Failed to read cached translations for "${lang}".`, error);
    return null;
  }
};

export const setCachedTranslationResources = (
  lang: string,
  resources: TranslationResources,
) => {
  try {
    localStorage.setItem(
      getCacheKey(lang),
      JSON.stringify({
        hash: getTranslationHash(resources),
        resources,
        savedAt: Date.now(),
      }),
    );
  } catch (error) {
    console.warn(`Failed to cache translations for "${lang}".`, error);
  }
};
